// /charts123 — cloutclub's revenue-analysis page, rebuilt for the one-time funnel.
//
// Same three views (hour by hour, day by day, week by week), the same
// Today/Yesterday/7-day/30-day comparison lines, the same red-amber-green table
// against the 30-day average, and the same AOV/ARPU/LTV cards underneath.
//
// It reads /admin/charts123/*, which is scoped to exactly the cohort /stats123
// reports on, so the two pages can never disagree about the numbers.
//
// Two of cloutclub's series had no honest equivalent here and were replaced —
// the reasons are in routes/admin.js next to the endpoints, and the page labels
// both so nobody reads them as the originals:
//   * "July 2025 Avg" -> "Launch Avg" (this funnel has no mature month yet)
//   * trailing-window LTV -> true cohort LTV by days since first charge
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Line, Bar } from 'react-chartjs-2';
import {
	Chart as ChartJS, CategoryScale, LinearScale, BarElement,
	PointElement, LineElement, Title, Tooltip, Legend,
} from 'chart.js';
import { API_BASE_URL } from '../config/api';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend);

const n2 = (v) => Number(v || 0).toFixed(2);
const money = (v) => `$${n2(v)}`;
const dash = (v, fmt = money) => (v === null || v === undefined ? '—' : fmt(v));

const formatHour = (h) => {
	if (h === 0) return '12:00 AM';
	if (h < 12) return `${h}:00 AM`;
	if (h === 12) return '12:00 PM';
	return `${h - 12}:00 PM`;
};

// cloutclub's thresholds: ±15% off the 30-day average is the band that counts as
// "normal". Future hours are greyed rather than shown as a 100% shortfall.
const cellStyle = (today, comparison, isFuture) => {
	if (isFuture) return { backgroundColor: '#e9ecef', color: '#495057' };
	if (!today) return { backgroundColor: '#f8f9fa', color: '#495057' };
	if (!comparison) return { backgroundColor: '#fff3cd', color: '#856404' };
	const diff = ((today - comparison) / comparison) * 100;
	if (diff >= 15) return { backgroundColor: '#d4edda', color: '#155724' };
	if (diff <= -15) return { backgroundColor: '#f8d7da', color: '#721c24' };
	return { backgroundColor: '#fff3cd', color: '#856404' };
};

const Delta = ({ today, comparison, isFuture }) => {
	if (isFuture || !today || !comparison) return null;
	const diff = ((today - comparison) / comparison) * 100;
	const color = diff >= 15 ? '#28a745' : diff <= -15 ? '#dc3545' : '#495057';
	return <><br /><small style={{ color }}>{diff >= 0 ? '↑' : '↓'} {Math.abs(diff).toFixed(1)}%</small></>;
};

const SERIES = [
	{ key: 'today', label: 'Today', color: '#FF0000', width: 4, dash: [5, 5], point: 6 },
	{ key: 'yesterday', label: 'Yesterday', color: '#36A2EB', width: 2 },
	{ key: 'avg_7_day', label: '7-Day Avg', color: '#4BC0C0', width: 2 },
	{ key: 'avg_30_day', label: '30-Day Avg', color: '#9966FF', width: 2 },
	{ key: 'avg_launch', label: 'Launch Avg', color: '#FF9F40', width: 2 },
];

// Only the active/inactive colours stay inline. Padding and font size live in the
// stylesheet below, because an inline style beats a media query and this page has to
// shrink on a phone.
const btn = (active) => ({
	background: active ? '#007bff' : '#fff', color: active ? '#fff' : '#007bff',
});

// Chart.js draws into a canvas in device pixels, so CSS cannot reach the point radii
// or the tick fonts — the 820px breakpoint has to exist in JS as well. Keep it in step
// with the one in the stylesheet below.
const NARROW = '(max-width: 820px)';
function useNarrow() {
	const [narrow, setNarrow] = useState(() => window.matchMedia(NARROW).matches);
	useEffect(() => {
		const mq = window.matchMedia(NARROW);
		const onChange = (e) => setNarrow(e.matches);
		mq.addEventListener('change', onChange);
		setNarrow(mq.matches);
		return () => mq.removeEventListener('change', onChange);
	}, []);
	return narrow;
}

export default function Charts123() {
	const [view, setView] = useState('hourly');
	const [type, setType] = useState('revenue');
	const [cumulative, setCumulative] = useState(false);
	const [hourly, setHourly] = useState(null);
	const [dw, setDw] = useState(null);
	const [met, setMet] = useState(null);
	// Scoped to the summary only: the charts above still plot every payment
	// method, because acquisition volume is not what the wallet path breaks.
	const [cardOnly, setCardOnly] = useState(false);
	const [metLoading, setMetLoading] = useState(false);
	const [err, setErr] = useState('');
	const [loading, setLoading] = useState(true);
	const narrow = useNarrow();
	const openIndex = useRef(null);

	// Safari's tab strip shows the title, not the URL, and every route here inherits
	// index.html's marketing title. Name the tab after the path so this page is
	// findable among a dozen open tabs; restore the default when routing away.
	useEffect(() => {
		const previous = document.title;
		document.title = 'viralgpt.com/charts123';
		return () => { document.title = previous; };
	}, []);

	useEffect(() => {
		setLoading(true);
		Promise.all([
			axios.get(`${API_BASE_URL}/admin/charts123/hourly`),
			axios.get(`${API_BASE_URL}/admin/charts123/daily-weekly`),
			axios.get(`${API_BASE_URL}/admin/charts123/metrics`, { params: { cardOnly } }),
		])
			.then(([h, d, m]) => { setHourly(h.data); setDw(d.data); setMet(m.data); setErr(''); })
			.catch((e) => setErr(e.response?.data?.error || e.message))
			.finally(() => setLoading(false));
		// Deliberately not keyed on cardOnly — the first load reads it once and the
		// effect below handles every change, so flipping the switch refetches the
		// summary alone rather than the whole page.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// The SPA serves one index.html, so every route otherwise shows the app
	// icon. Swap in a chart glyph while this page is mounted and put the
	// original back on the way out, so the tab is findable among a dozen others.
	useEffect(() => {
		let link = document.querySelector("link[rel~='icon']");
		if (!link) {
			link = document.createElement('link');
			link.rel = 'icon';
			document.head.appendChild(link);
		}
		const previous = link.getAttribute('href');
		link.setAttribute('href', 'data:image/svg+xml,' + encodeURIComponent(
			'<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><text y=".9em" font-size="90">\uD83D\uDCCA</text></svg>'
		));
		return () => {
			if (previous === null) link.removeAttribute('href');
			else link.setAttribute('href', previous);
		};
	}, []);

	const firstMetricLoad = useRef(true);
	useEffect(() => {
		if (firstMetricLoad.current) { firstMetricLoad.current = false; return; }
		setMetLoading(true);
		axios.get(`${API_BASE_URL}/admin/charts123/metrics`, { params: { cardOnly } })
			.then((m) => { setMet(m.data); setErr(''); })
			.catch((e) => setErr(e.response?.data?.error || e.message))
			.finally(() => setMetLoading(false));
	}, [cardOnly]);

	const isRev = type === 'revenue';
	const suffix = isRev ? '_revenue' : '_transactions';
	const fmt = isRev ? money : ((v) => String(v));

	// Non-cumulative rows carry no is_future flag — an hour is future if it has
	// not been reached yet, which is the same test the cumulative builder used.
	const rows = useMemo(() => {
		if (!hourly) return [];
		const src = cumulative
			? (isRev ? hourly.revenueCumulative : hourly.transactionCumulative)
			: hourly.revenueResults;
		return src.map((r) => ({
			...r,
			is_future: cumulative ? r.is_future : r.hour_cst > hourly.currentHourCST,
		}));
	}, [hourly, cumulative, isRev]);

	const hourlyChart = useMemo(() => ({
		labels: rows.map((r) => `${r.hour_cst}:00`),
		datasets: SERIES.map((s) => ({
			label: s.label,
			data: rows.map((r) => (s.key === 'today' && r.is_future ? null : r[s.key + suffix])),
			borderColor: s.color,
			backgroundColor: s.color,
			borderWidth: narrow ? s.width * 0.6 : s.width,
			borderDash: s.dash || [],
			// 24 hours across a phone leaves ~14px per point, so a radius-6 marker is wider
			// than its own spacing. Today shrinks; the comparison lines drop their dots
			// altogether — index-mode tooltips still read every series without them.
			pointRadius: narrow ? (s.point ? 2.5 : 0) : (s.point || 2),
			pointHitRadius: 12,
			tension: s.key === 'today' ? 0.1 : 0.4,
			spanGaps: false,
		})),
	}), [rows, suffix, narrow]);

	const daily = dw?.dailyResults || [];
	const weekly = dw?.weeklyResults || [];

	const barChart = (src, labelOf) => ({
		labels: [...src].reverse().map(labelOf),
		datasets: [
			{ label: 'New Sales', data: [...src].reverse().map((r) => r.total_revenue), backgroundColor: '#36A2EB', borderColor: '#000', borderWidth: 1 },
			{ label: 'Renewal', data: [...src].reverse().map((r) => r.renewal_revenue), backgroundColor: '#00ff14', borderColor: '#000', borderWidth: 1 },
		],
	});

	// On a phone the axis titles and the full-size legend cost more height than the
	// plot they annotate, so both go; the axis units are still in the chart title.
	const chartOpts = (title, yLabel, prefix, xLabel) => ({
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			title: { display: true, text: title, font: { size: narrow ? 12 : 16 } },
			legend: {
				position: 'top',
				labels: narrow ? { boxWidth: 10, boxHeight: 10, padding: 6, font: { size: 10 } } : {},
			},
		},
		scales: {
			x: {
				title: { display: !narrow, text: xLabel },
				ticks: narrow ? { font: { size: 9 }, maxRotation: 0, autoSkipPadding: 10 } : {},
			},
			y: {
				beginAtZero: true,
				title: { display: !narrow && !!yLabel, text: yLabel },
				ticks: { callback: (v) => prefix + v, ...(narrow ? { font: { size: 9 } } : {}) },
			},
		},
		interaction: { intersect: false, mode: 'index' },
		// Touch has no mouseout, so without this the tooltip sticks until the next tap
		// moves it. touchstart opens it and this click — which fires afterwards — closes
		// it again when the same column is tapped twice. Pointers keep hover behaviour.
		onClick: (_evt, elements, chart) => {
			if (!narrow) return;
			const index = elements?.length ? elements[0].index : null;
			if (index === null) return;
			if (openIndex.current === index) {
				openIndex.current = null;
				chart.setActiveElements([]);
				chart.tooltip?.setActiveElements([], { x: 0, y: 0 });
				chart.update();
			} else {
				openIndex.current = index;
			}
		},
	});

	const m = met?.metrics;

	return (
		<div className="c123">
			{/* Same escape hatch Stats123 uses: `all: revert` drops Bootstrap and the
			    global stylesheet on this subtree so the page renders like cloutclub's
			    plain HTML, then only the rules cloutclub declares are re-applied.
			    Inline styles still win, which is what keeps the RAG cells coloured. */}
			<style>{`
				.c123, .c123 * { all: revert; }
				.c123 { padding: 15px; font-family: Arial, sans-serif; }
				.c123 table, .c123 th, .c123 td { border: 1px solid #ddd; padding: 8px; text-align: center; }
				.c123 table { border-collapse: collapse; width: 100%; margin-top: 20px; }
				.c123 th { background-color: #f2f2f2; }
				.c123 h2 { margin-top: 40px; }
				.c123 .charthold { width: 100%; height: 520px; margin: 20px 0; position: relative; }
				.c123 .cards { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; margin: 20px 0; }
				.c123 button { padding: 6px 14px; margin: 0 4px; font-size: 14px; cursor: pointer; border: 1px solid #007bff; border-radius: 5px; }
				.c123 .vmode { text-align: center; margin: 15px 0; padding: 10px; background: #f8f9fa; border-radius: 8px; }
				.c123 .toggles { text-align: center; margin: 15px 0; }
				.c123 .cumtoggle { text-align: center; margin: 10px 0; }
				.c123 .cumtoggle label { display: inline-flex; align-items: center; gap: 5px; cursor: pointer; font-size: 14px; white-space: nowrap; }
				.c123 .card { border: 1px solid #ddd; padding: 15px; border-radius: 8px; background: #f9f9f9; }
				.c123 .card .ct { font-weight: bold; font-size: 18px; text-align: center; margin-bottom: 10px; }
				.c123 .card .cv { font-size: 24px; text-align: center; margin: 5px 0; }
				.c123 .card .cr { font-size: 14px; text-align: center; }
				@media (max-width: 820px) {
					.c123 { padding: 6px; }
					.c123 table { font-size: 9px; margin-top: 10px; }
					.c123 th, .c123 td { padding: 3px; }
					.c123 h1 { font-size: 20px; }
					.c123 h2 { font-size: 17px; margin-top: 20px; }
					.c123 button { padding: 5px 9px; margin: 0 3px; font-size: 12px; }
					.c123 .vmode { margin: 10px 0; padding: 7px; }
					.c123 .toggles { margin: 10px 0; }
					.c123 .cumtoggle { margin: 8px 0; }
					.c123 .cumtoggle label { font-size: 12px; }
					.c123 .cards { grid-template-columns: 1fr; gap: 10px; margin: 12px 0; }
					.c123 .charthold { height: 340px; margin: 10px 0; }
					.c123 .card { padding: 10px; }
					.c123 .card .ct { font-size: 15px; margin-bottom: 6px; }
					.c123 .card .cv { font-size: 20px; }
					.c123 .card .cr { font-size: 13px; }
					.c123 .card small { font-size: 11px; }
				}
			`}</style>

			<h1 style={{ marginTop: 0 }}>Revenue Analysis — One-Time Funnel</h1>
			<div style={{ fontSize: 13, color: '#666' }}>
				Package buyers only (no subscription row), from {dw?.funnelStart || met?.funnelStart || '—'}. Same cohort as /stats123.
			</div>

			<div className="vmode">
				<strong style={{ marginRight: 10 }}>View Mode:</strong>
				<button style={btn(view === 'hourly')} onClick={() => setView('hourly')}>Hour by Hour</button>
				<button style={btn(view === 'daily')} onClick={() => setView('daily')}>Day by Day</button>
				<button style={btn(view === 'weekly')} onClick={() => setView('weekly')}>Week by Week</button>
			</div>

			{err && <div style={{ background: '#ffe6e6', border: '1px solid #cc0000', padding: 8, marginBottom: 10, fontSize: 13 }}>{err}</div>}
			{loading && <div style={{ fontSize: 13 }}>Loading…</div>}

			{!loading && view === 'hourly' && hourly && (
				<>
					<div className="toggles">
						<button style={btn(isRev)} onClick={() => setType('revenue')}>Revenue</button>
						<button style={btn(!isRev)} onClick={() => setType('transactions')}>Transactions</button>
					</div>
					{/* Own line, as on cloutclub — sharing the row with the buttons wrapped the
					    label mid-phrase on a phone. */}
					<div className="cumtoggle">
						<label>
							<input type="checkbox" checked={cumulative} onChange={() => setCumulative((v) => !v)} />
							Show Cumulative (Running Total)
						</label>
					</div>

					<div className="charthold">
						<Line
							data={hourlyChart}
							options={chartOpts(
								`Hourly ${isRev ? 'Revenue' : 'Transaction'} Comparison${cumulative ? ' (Cumulative)' : ''}`,
								isRev ? (cumulative ? 'Cumulative Revenue ($)' : 'Revenue ($)') : (cumulative ? 'Cumulative Transactions' : 'Transactions'),
								isRev ? '$' : '',
								'Hour (CST)',
							)}
						/>
					</div>

					<table>
						<thead>
							<tr>
								<th>Hour (CST)</th>
								{SERIES.map((s) => <th key={s.key}>{s.label}</th>)}
							</tr>
						</thead>
						<tbody>
							{rows.map((r) => {
								const todayVal = r.is_future ? 0 : r[`today${suffix}`];
								const cmp = r[`avg_30_day${suffix}`];
								return (
									<tr key={r.hour_cst}>
										<td style={{ fontWeight: 500 }}>{formatHour(r.hour_cst)}</td>
										<td style={cellStyle(todayVal, cmp, r.is_future)}>
											<strong>{r.is_future || !todayVal ? '-' : fmt(todayVal)}</strong>
											<Delta today={todayVal} comparison={cmp} isFuture={r.is_future} />
										</td>
										{SERIES.slice(1).map((s) => (
											<td key={s.key} style={{ color: '#495057' }}>{fmt(r[s.key + suffix])}</td>
										))}
									</tr>
								);
							})}
						</tbody>
					</table>
				</>
			)}

			{!loading && view === 'daily' && dw && (
				<>
					<h2>Daily New Sales (since launch)</h2>
					<div className="charthold">
						<Bar data={barChart(daily, (r) => r.date.slice(5))} options={chartOpts('Daily New-Sale Revenue', '', '$', 'Date')} />
					</div>
					<table>
						<thead>
							<tr><th>Date</th><th>New Sales</th><th>Renewal</th><th>Topups</th><th>Transactions</th><th>New Customers</th><th>Avg Order Value</th></tr>
						</thead>
						<tbody>
							{daily.map((r) => (
								<tr key={r.date}>
									<td>{r.date}</td>
									<td><strong>{money(r.total_revenue)}</strong></td>
									<td>{money(r.renewal_revenue)}</td>
									<td>{money(r.topup_revenue)}</td>
									<td>{r.total_transactions}</td>
									<td>{r.new_customers}</td>
									<td>{money(r.aov)}</td>
								</tr>
							))}
						</tbody>
					</table>
				</>
			)}

			{!loading && view === 'weekly' && dw && (
				<>
					<h2>Weekly New Sales (since launch)</h2>
					<div className="charthold">
						<Bar data={barChart(weekly, (r) => r.week_start.slice(5))} options={chartOpts('Weekly New-Sale Revenue', '', '$', 'Week Starting')} />
					</div>
					<table>
						<thead>
							<tr><th>Date Range</th><th>New Sales</th><th>Renewal</th><th>Topups</th><th>Transactions</th><th>New Customers</th><th>Avg Order Value</th></tr>
						</thead>
						<tbody>
							{weekly.map((w) => (
								<tr key={w.year_week}>
									<td>{w.week_start} → {w.week_end}</td>
									<td><strong>{money(w.total_revenue)}</strong></td>
									<td>{money(w.renewal_revenue)}</td>
									<td>{money(w.topup_revenue)}</td>
									<td>{w.total_transactions}</td>
									<td>{w.new_customers}</td>
									<td>{money(w.aov)}</td>
								</tr>
							))}
						</tbody>
					</table>
				</>
			)}

			<h2>AOV, ARPU &amp; LTV Summary</h2>
			<label style={{
				display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer',
				padding: '8px 12px', marginBottom: 12, borderRadius: 8,
				border: '1px solid ' + (cardOnly ? '#007bff' : '#ddd'),
				background: cardOnly ? '#e7f1ff' : '#f8f9fa',
			}}>
				<input type="checkbox" checked={cardOnly} onChange={() => setCardOnly((v) => !v)} />
				<span style={{ fontWeight: 'bold' }}>Card payments only</span>
				<span style={{ fontSize: 12, color: '#666' }}>
					{metLoading ? 'loading…' : 'excludes Apple Pay buyers, whose renewals are unreliable'}
				</span>
			</label>
			{met && m && (
				<div className="cards">
					<div className="card">
						<div className="ct">AOV (Average Order Value)</div>
						<div className="cv">{dash(m.today.aov)}</div>
						<div className="cr">Yesterday: {dash(m.yesterday.aov)}</div>
						<div className="cr">7-Day Avg: {dash(m.d7.aov)}</div>
						<div className="cr">30-Day Avg: {dash(m.d30.aov)}</div>
						<div className="cr">Launch Avg: {dash(m.launch.aov)}</div>
						<small>First-order value: package plus the add-ons bought with it, per package sold. Excludes renewals and top-ups, so it moves only when pricing or the package mix changes.</small>
					</div>
					<div className="card">
						<div className="ct">ARPU (Avg Revenue Per User)</div>
						<div className="cv">{dash(m.today.arpu)}</div>
						<div className="cr">Yesterday: {dash(m.yesterday.arpu)}</div>
						<div className="cr">7-Day Avg: {dash(m.d7.arpu)}</div>
						<div className="cr">30-Day Avg: {dash(m.d30.arpu)}</div>
						<div className="cr">Launch Avg: {dash(m.launch.arpu)}</div>
						<small>Package, add-ons and dashboard top-ups per distinct buyer. Excludes auto-refill renewals, which bill on a schedule rather than being bought. Its gap above AOV is the repeat purchasing.</small>
					</div>
					<div className="card">
						<div className="ct">LTV (Cohort, by day)</div>
						{met.ltv.map((b) => (
							<div key={b.days} className="cr">
								Day {b.days}: {dash(b.ltv)}{' '}
								<span style={{ color: '#888' }}>({b.cohort_users} matured)</span>
							</div>
						))}
						<small>
							Average spend within D days of a buyer's first charge, counting only buyers
							whose D days have elapsed. "—" means no cohort is old enough yet.
							Each bucket lands just after a refill cycle rather than on it: the refill
							bills on day 7 and every 28 after, so Day 9 is cycle 1 collected, Day 40
							cycle 2 and Day 68 cycle 3, each with slack for charge timing and retries.
							Day 30 matches the /stats123 projection horizon.
							Counts every stream: package, add-ons, renewals and top-ups. Wallet buyers
							whose first renewal came due before the wallet path billed reliably are
							excluded, the same way /stats123 excludes them; card buyers never are.
						</small>
					</div>
					{met.retention && (
						<div className="card">
							<div className="ct">Refill retention by pack</div>
							{met.retention.map((r) => (
								<div key={r.pack} className="cr">
									<strong>{r.pack}</strong>: cycle 1 {r.cycle1_rate === null ? '—' : r.cycle1_rate + '%'}
									{' → '}cycle 2 {r.cycle2_rate === null ? '—' : r.cycle2_rate + '%'}{' '}
									<span style={{ color: '#888' }}>
										({r.cycle1_paid}/{r.cycle1_users}, then {r.cycle2_paid}/{r.cycle2_users})
									</span>
								</div>
							))}
							<small>
								Cycle 1 is the share of buyers who paid their first refill. Cycle 2 is the
								share of <em>those</em> payers who paid the second — cycle-over-cycle
								retention, which is what a 6-month LTV turns on. Each rate counts only
								buyers old enough to have reached that bill, so a young cohort is not
								scored as having skipped it. Cycle 2 bills on day 35, so it starts
								filling in once cohorts pass {met.cycleEnds ? 'day ' + met.cycleEnds.cycle2 : 'day 40'}.
							</small>
						</div>
					)}
				</div>
			)}
		</div>
	);
}
